import { useDrawingInteraction } from "../hooks/useDrawingInteraction";
import { useDrawingStorage } from "../hooks/useDrawingStorage";
import { DOCK_STATE_KEY, DRAWINGS_KEY_PREFIX, cloneDrawingSnapshot, createLocalStorageAdapter, getSelectedObjectIds, } from "@vninvestcharts/engine-drawing";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
function cloneDrawings(drawings) {
    return drawings.map((drawing) => cloneDrawingSnapshot(drawing));
}
function areDrawingsEqual(left, right) {
    if (left === right) {
        return true;
    }
    if (left.length !== right.length) {
        return false;
    }
    for (let index = 0; index < left.length; index += 1) {
        const leftDrawing = left[index];
        const rightDrawing = right[index];
        if (leftDrawing.id !== rightDrawing.id
            || leftDrawing.type !== rightDrawing.type
            || leftDrawing.paneId !== rightDrawing.paneId
            || leftDrawing.yScaleId !== rightDrawing.yScaleId
            || leftDrawing.text !== rightDrawing.text
            || leftDrawing.zIndex !== rightDrawing.zIndex
            || leftDrawing.locked !== rightDrawing.locked
            || leftDrawing.visible !== rightDrawing.visible
            || JSON.stringify(leftDrawing.points) !== JSON.stringify(rightDrawing.points)
            || JSON.stringify(leftDrawing.style) !== JSON.stringify(rightDrawing.style)
            || JSON.stringify(leftDrawing.fibLevels ?? null) !== JSON.stringify(rightDrawing.fibLevels ?? null)
            || JSON.stringify(leftDrawing.fibLevelConfigs ?? null) !== JSON.stringify(rightDrawing.fibLevelConfigs ?? null)
            || JSON.stringify(leftDrawing.riskReward ?? null) !== JSON.stringify(rightDrawing.riskReward ?? null)
            || JSON.stringify(leftDrawing.segmentStyles ?? null) !== JSON.stringify(rightDrawing.segmentStyles ?? null)
            || JSON.stringify(leftDrawing.segmentVisible ?? null) !== JSON.stringify(rightDrawing.segmentVisible ?? null)
            || leftDrawing.clonedFrom !== rightDrawing.clonedFrom
            || leftDrawing.groupId !== rightDrawing.groupId
            || leftDrawing.symbol !== rightDrawing.symbol
            || leftDrawing.timeframe !== rightDrawing.timeframe
            || leftDrawing.extendLeft !== rightDrawing.extendLeft
            || leftDrawing.extendRight !== rightDrawing.extendRight
            || leftDrawing.showMeasurement !== rightDrawing.showMeasurement
            || JSON.stringify(leftDrawing.alertCondition ?? null) !== JSON.stringify(rightDrawing.alertCondition ?? null)
            || JSON.stringify(leftDrawing.alertConditions ?? null) !== JSON.stringify(rightDrawing.alertConditions ?? null)
            || JSON.stringify(leftDrawing.waveLabels ?? null) !== JSON.stringify(rightDrawing.waveLabels ?? null)
            || JSON.stringify(leftDrawing.harmonicRatios ?? null) !== JSON.stringify(rightDrawing.harmonicRatios ?? null)
            || leftDrawing.gannSquareSize !== rightDrawing.gannSquareSize
            || JSON.stringify(leftDrawing.cycleParams ?? null) !== JSON.stringify(rightDrawing.cycleParams ?? null)
            || leftDrawing.createdAt !== rightDrawing.createdAt) {
            return false;
        }
    }
    return true;
}
function getSelectedDrawing(drawings, selectedIds) {
    if (selectedIds.length === 0) {
        return null;
    }
    return drawings.find((drawing) => drawing.id === selectedIds[0]) ?? null;
}
const DOCK_STATE_STORAGE_KEY = DOCK_STATE_KEY;
function isFiniteNumber(value) {
    return typeof value === "number" && Number.isFinite(value);
}
function parseDockPosition(value) {
    if (typeof value !== "object" || value === null)
        return null;
    const candidate = value;
    if (isFiniteNumber(candidate.x) && isFiniteNumber(candidate.y)) {
        return { x: candidate.x, y: candidate.y };
    }
    return null;
}
// Must mirror runtime resize clamps in DrawingDock.tsx (base width 360, height 200).
const DOCK_SIZE_DELTA_MIN_W = 280 - 360;
const DOCK_SIZE_DELTA_MAX_W = 600 - 360;
const DOCK_SIZE_DELTA_MIN_H = 200 - 200;
const DOCK_SIZE_DELTA_MAX_H = 800 - 200;
function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
}
function parseDockSizeDelta(value) {
    if (typeof value === "object" && value !== null) {
        const candidate = value;
        if (isFiniteNumber(candidate.w) && isFiniteNumber(candidate.h)) {
            return {
                w: clamp(candidate.w, DOCK_SIZE_DELTA_MIN_W, DOCK_SIZE_DELTA_MAX_W),
                h: clamp(candidate.h, DOCK_SIZE_DELTA_MIN_H, DOCK_SIZE_DELTA_MAX_H),
            };
        }
    }
    return { w: 0, h: 0 };
}
function loadDockState() {
    try {
        const raw = localStorage.getItem(DOCK_STATE_STORAGE_KEY);
        if (!raw)
            return null;
        const parsed = JSON.parse(raw);
        if (typeof parsed !== "object" || parsed === null)
            return null;
        const tab = parsed.activeTab;
        if (tab !== "properties" && tab !== "objects" && tab !== "templates")
            return null;
        return {
            isOpen: Boolean(parsed.isOpen),
            activeTab: tab,
            position: parseDockPosition(parsed.position),
            sizeDelta: parseDockSizeDelta(parsed.sizeDelta),
        };
    }
    catch {
        return null;
    }
}
function saveDockState(state) {
    try {
        localStorage.setItem(DOCK_STATE_STORAGE_KEY, JSON.stringify(state));
        return true;
    }
    catch (error) {
        // P1-03: quota or private browsing — warn instead of swallowing so the
        // controller can surface it once (see dockSaveErrorNotifiedRef).
        console.warn("Failed to persist drawing dock state:", error);
        return false;
    }
}
export function useDrawingWorkspaceController({ defaultDockTab = "properties", defaultDrawings, drawings, drawingStorageKey, drawingStorageAdapter, enabled = true, onDrawingsChange, instanceId, onStorageError, symbol, timeframe, }) {
    const isControlled = drawings !== undefined;
    const initialDrawingsRef = useRef(drawings ?? defaultDrawings ?? []);
    const interaction = useDrawingInteraction(initialDrawingsRef.current);
    const [activeTool, setActiveTool] = useState("cursor");
    const [clipboard, setClipboard] = useState(null);
    const [dock, setDock] = useState(() => {
        const persisted = loadDockState();
        return persisted ?? { isOpen: false, activeTab: defaultDockTab, position: null, sizeDelta: { w: 0, h: 0 } };
    });
    // Persist dock state to localStorage on every change. Failures notify once
    // (not on every keystroke/drag) until a save succeeds again.
    const dockSaveErrorNotifiedRef = useRef(false);
    const onStorageErrorRef = useRef(onStorageError);
    onStorageErrorRef.current = onStorageError;
    useEffect(() => {
        if (!saveDockState(dock)) {
            if (!dockSaveErrorNotifiedRef.current) {
                dockSaveErrorNotifiedRef.current = true;
                onStorageErrorRef.current?.({ operation: "dock-save", error: new Error("Drawing dock state could not be persisted") });
            }
        }
        else {
            dockSaveErrorNotifiedRef.current = false;
        }
    }, [dock]);
    const syncingFromExternalRef = useRef(false);
    const lastControlledDrawingsRef = useRef(drawings);
    const lastEmittedDrawingsRef = useRef(null);
    // M3: a host that writes `onDrawingsChange={(d) => setState(d)}` produces a new
    // function identity on every render. Anything that takes the raw prop as a
    // `useCallback`/`useEffect` dependency then re-runs on every render, and this
    // hook feeds that callback straight into `useDrawingStorage` as `onLoad`.
    // Measured, not inferred: with an inline arrow the LOAD effect at
    // useDrawingStorage.ts:66 re-ran on every render, each pass called
    // `onLoad` -> `reconcileDrawings` -> dispatch -> a fresh `allDrawings` array
    // -> another render, and the test worker died with `ERR_WORKER_OUT_OF_MEMORY`.
    //
    // The divergence starts here, at the prop boundary, so that is where it is
    // fixed: read the callback through a ref that never enters a dependency
    // array.
    //
    // The ref is refreshed in an EFFECT, not in the render body. An earlier
    // version assigned `onDrawingsChangeRef.current = onDrawingsChange` inline,
    // which works but is a render-phase side effect: React does not guarantee a
    // render is committed (transitions can be thrown away, Suspense can hide it),
    // and a thrown-away render would still have mutated the ref — so a committed
    // effect could emit through a callback that captured a render React discarded.
    // Declaring the assignment effect BEFORE the emit effect is enough: React runs
    // a component's effects in declaration order, so the emit effect always reads
    // the latest committed callback.
    //
    // The sibling assignments at :245 (`onStorageErrorRef`) and in
    // `useDrawingStorage` are left as they are: they are pre-existing and outside
    // this fix. Changing them here would widen the diff past what was measured.
    const onDrawingsChangeRef = useRef(onDrawingsChange);
    useEffect(() => {
        onDrawingsChangeRef.current = onDrawingsChange;
    });
    const storageAdapter = useMemo(() => drawingStorageAdapter
        ?? createLocalStorageAdapter(
        // P1-09: explicit key wins; otherwise opt-in instance namespace.
        drawingStorageKey ?? (instanceId ? `${DRAWINGS_KEY_PREFIX}-${instanceId}` : undefined)), [drawingStorageAdapter, drawingStorageKey, instanceId]);
    const allDrawings = interaction.allDrawings;
    const replaceDrawings = interaction.replaceDrawings;
    const reconcileDrawings = interaction.reconcileDrawings;
    const resetScope = interaction.resetScope;
    // P3-07: hydrating from storage is NOT a user intent. Dispatching it as
    // REPLACE pushed an undo step and cleared the redo stack, so two measured
    // failures followed:
    //
    //   - opening a chart with saved drawings and pressing Ctrl+Z once deleted
    //     them, undoing a LOAD the user never performed;
    //   - drawing, undoing, then switching symbol/timeframe destroyed the redo
    //     the user still had.
    //
    // The first is the same defect as the anchor reconciliation this session
    // fixed; leaving it would mean the invariant ("undo history records only
    // user intent") is still violated one function over.
    //
    // M2 adds the scope dimension. RECONCILE keeps `past`/`future` by design,
    // which is right for re-anchoring the SAME symbol's drawings and wrong for
    // loading a DIFFERENT symbol's: `present` becomes B's drawings while `past`
    // still holds A's, so one Ctrl+Z on B pops A back onto the screen
    // (reproduced in useDrawingWorkspaceController.undoScope.test.tsx).
    //
    // The discriminator is the scope the timeline was last recorded against, not
    // whether a load happened — a reload of the same symbol (StrictMode
    // double-mount, a storage write landing mid-session) must NOT throw the
    // user's undo away, which is exactly what dispatching RESET_SCOPE
    // unconditionally would do.
    const timelineScopeRef = useRef(`${symbol}|${timeframe}`);
    const handleStorageLoad = useCallback((loadedDrawings) => {
        if (isControlled) {
            onDrawingsChangeRef.current?.(cloneDrawings(loadedDrawings));
            return;
        }
        const scope = `${symbol}|${timeframe}`;
        if (timelineScopeRef.current !== scope) {
            timelineScopeRef.current = scope;
            resetScope(loadedDrawings);
            return;
        }
        reconcileDrawings(loadedDrawings);
    }, [isControlled, reconcileDrawings, resetScope, symbol, timeframe]);
    const storage = useDrawingStorage(symbol, timeframe, allDrawings, handleStorageLoad, storageAdapter, {
        enabled: enabled && !isControlled,
        onStorageError: (info) => onStorageErrorRef.current?.(info),
    });
    useEffect(() => {
        if (!isControlled) {
            lastControlledDrawingsRef.current = drawings;
            return;
        }
        const nextDrawings = drawings ?? [];
        const previousControlledDrawings = lastControlledDrawingsRef.current ?? [];
        lastControlledDrawingsRef.current = nextDrawings;
        if (areDrawingsEqual(previousControlledDrawings, nextDrawings)) {
            return;
        }
        if (areDrawingsEqual(nextDrawings, allDrawings)) {
            return;
        }
        syncingFromExternalRef.current = true;
        // M2 (controlled half). A controlled host passes `drawings` as a public
        // prop (`VNInvestChartsProps.drawings`) and storage is disabled entirely
        // in that mode, so `handleStorageLoad` returns before it can reach
        // `resetScope`. This effect is the controlled-mode twin of that function
        // and it is the ONLY place the host's scope change lands here — without
        // this branch a controlled consumer gets the same cross-symbol undo leak
        // the storage path just had fixed.
        //
        // The scope is compared the same way, and against the same ref, so the
        // two paths cannot disagree about what "the current scope" is.
        const scope = `${symbol}|${timeframe}`;
        if (timelineScopeRef.current !== scope) {
            timelineScopeRef.current = scope;
            resetScope(nextDrawings);
            return;
        }
        replaceDrawings(nextDrawings);
    }, [allDrawings, drawings, isControlled, replaceDrawings, resetScope, symbol, timeframe]);
    useEffect(() => {
        const emit = onDrawingsChangeRef.current;
        if (!emit || (!enabled && !isControlled)) {
            return;
        }
        if (syncingFromExternalRef.current) {
            syncingFromExternalRef.current = false;
            lastEmittedDrawingsRef.current = allDrawings;
            return;
        }
        if (lastEmittedDrawingsRef.current !== null && areDrawingsEqual(lastEmittedDrawingsRef.current, allDrawings)) {
            return;
        }
        lastEmittedDrawingsRef.current = allDrawings;
        emit(cloneDrawings(allDrawings));
        // `onDrawingsChange` is deliberately absent: it is read through
        // `onDrawingsChangeRef`, whose identity is stable. Listing the raw prop
        // here would re-arm this effect on every render of a host that passes an
        // inline arrow, which is the second half of the M3 loop — see the note on
        // `onDrawingsChangeRef` above for the measurement.
    }, [allDrawings, enabled, isControlled]);
    const selectedIds = useMemo(() => getSelectedObjectIds(interaction.drawingState), [interaction.drawingState]);
    const selectedDrawing = useMemo(() => getSelectedDrawing(allDrawings, selectedIds), [allDrawings, selectedIds]);
    const copySelected = useCallback(() => {
        if (!selectedDrawing) {
            return;
        }
        setClipboard(cloneDrawingSnapshot(selectedDrawing));
    }, [selectedDrawing]);
    const clearClipboard = useCallback(() => {
        setClipboard(null);
    }, []);
    const openDock = useCallback((tab) => {
        setDock((current) => ({
            ...current,
            isOpen: true,
            activeTab: tab ?? current.activeTab,
        }));
    }, []);
    const closeDock = useCallback(() => {
        setDock((current) => ({ ...current, isOpen: false }));
    }, []);
    const setDockTab = useCallback((tab) => {
        setDock((current) => ({ ...current, isOpen: true, activeTab: tab }));
    }, []);
    const setDockLayout = useCallback((layout) => {
        setDock((current) => ({
            ...current,
            ...(layout.position !== undefined ? { position: layout.position } : {}),
            ...(layout.sizeDelta !== undefined ? { sizeDelta: layout.sizeDelta } : {}),
        }));
    }, []);
    const selectDrawing = useCallback((objectId) => {
        if (!objectId) {
            interaction.cancelDrawing();
            return;
        }
        interaction.selectObject(objectId);
    }, [interaction]);
    const cloneClipboard = useCallback(() => {
        return clipboard ? cloneDrawingSnapshot(clipboard) : null;
    }, [clipboard]);
    const state = useMemo(() => ({
        activeTool,
        clipboard,
        dock,
        drawings: allDrawings,
        selectedDrawing,
        selectedIds,
    }), [activeTool, allDrawings, clipboard, dock, selectedDrawing, selectedIds]);
    // P4-02: toolbar-visible signature. Subscribers re-render only when the
    // active tool or undo/redo availability flips (not on every drawing edit).
    const toolbarSignature = `${activeTool}|${interaction.canUndo}|${interaction.canRedo}`;
    const toolbarListenersRef = useRef(new Set());
    const toolbarSignatureRef = useRef(toolbarSignature);
    const subscribe = useCallback((listener) => {
        toolbarListenersRef.current.add(listener);
        return () => {
            toolbarListenersRef.current.delete(listener);
        };
    }, []);
    useEffect(() => {
        if (toolbarSignatureRef.current === toolbarSignature)
            return;
        toolbarSignatureRef.current = toolbarSignature;
        toolbarListenersRef.current.forEach((listener) => {
            try {
                listener();
            }
            catch {
                // A broken toolbar listener must never break the chart.
            }
        });
    });
    const controllerRef = useRef(null);
    if (controllerRef.current === null) {
        controllerRef.current = {
            interaction,
            storage,
            state,
            setActiveTool,
            setClipboard,
            copySelected,
            clearClipboard,
            openDock,
            closeDock,
            setDockTab,
            setDockLayout,
            selectDrawing,
            cloneClipboard,
            subscribe,
        };
    }
    else {
        controllerRef.current.interaction = interaction;
        controllerRef.current.storage = storage;
        controllerRef.current.state = state;
        controllerRef.current.setActiveTool = setActiveTool;
        controllerRef.current.setClipboard = setClipboard;
        controllerRef.current.copySelected = copySelected;
        controllerRef.current.clearClipboard = clearClipboard;
        controllerRef.current.openDock = openDock;
        controllerRef.current.closeDock = closeDock;
        controllerRef.current.setDockTab = setDockTab;
        controllerRef.current.setDockLayout = setDockLayout;
        controllerRef.current.selectDrawing = selectDrawing;
        controllerRef.current.cloneClipboard = cloneClipboard;
        controllerRef.current.subscribe = subscribe;
    }
    return controllerRef.current;
}
